import FeatureRoute from '@components/FeatureRoute'
import { SettingsLayout } from '@layouts/SettingsLayout'
import ComplementaryItemsPage from '@pages/membership/ComplementaryItemsPage'
import AdminInvitesPage from '@pages/settings/admins/AdminInvitesPage'
import AdminsPage from '@pages/settings/admins/AdminsPage'
import RolesAdminsPage from '@pages/settings/admins/RolesAdminsPage'
import ExternalBookingAPIPage from '@pages/settings/api/ExternalBookingAPIPage'
import AvailabilityViewerPage from '@pages/settings/booking/AvailabilityViewerPage'
import BookingNotificationDetailPage from '@pages/settings/booking/BookingNotificationDetailPage'
import BookingNotificationsPage from '@pages/settings/booking/BookingNotificationsPage'
import BookingSettingsPage from '@pages/settings/booking/BookingSettingsPage'
import PaymentLinkPage from '@pages/settings/booking/PaymentLink'
import SeatingAreasPage from '@pages/settings/booking/SeatingAreasPage'
import BusinessLoyaltyTierPage from '@pages/settings/business/BusinessLoyaltyTierPage'
import BusinessNotificationsPage from '@pages/settings/business/BusinessNotificationsPage'
import BusinessProfilStatusesPage from '@pages/settings/business/BusinessProfileStatusesPage'
import BusinessVerifierPage from '@pages/settings/business/BusinessVerifierPage'
import CompanyStatusesPage from '@pages/settings/business/CompanyStatusesPage'
import IntegrationsAdyenPage from '@pages/settings/integrations/IntegrationsAdyenPage'
import IntegrationsDominoConfigsPage from '@pages/settings/integrations/IntegrationsDominoConfigsPage'
import IntegrationsSevenroomsPage from '@pages/settings/integrations/IntegrationsSevenroomsPage'
import IntegrationsUberPage from '@pages/settings/integrations/IntegrationsUberPage'
import IntegrationsWebBookingPage from '@pages/settings/integrations/IntegrationsWebBookingPage'
import MembershipBookletsPage from '@pages/settings/membership/MembershipBookletsPage'
import MembershipNotificationDetailPage from '@pages/settings/membership/MembershipNotificationDetailPage'
import MembershipNotificationsPage from '@pages/settings/membership/MembershipNotificationsPage'
import MembershipProfileTagsPage from '@pages/settings/membership/MembershipProfileTagsPage'
import ParticipationStatusesPage from '@pages/settings/membership/ParticipationStatusesPage'
import SubscriptionTagsPage from '@pages/settings/membership/SubscriptionTagsPage'
import SettingsPage from '@pages/settings/SettingsPage'
import UserDataPrivacyPage from '@pages/settings/user/UserDataPrivacyPage'
import { Navigate, RouteObject } from 'react-router-dom'
import legalDocumentRoutes from './legal-documents/LegalDocumentRoutes'
import platformsRoutes from './platforms/PlatformsRoutes'
import programRoutes from './programs/ProgramRoutes'

const settingsRoutes: RouteObject[] = [
  {
    element: <SettingsLayout />,
    children: [
      { path: 'list', element: <SettingsPage /> },
      { path: 'platforms', children: platformsRoutes },
      { path: 'admins', element: <AdminsPage /> },
      { path: 'admins/invites', element: <AdminInvitesPage /> },
      { path: 'admins/roles', element: <RolesAdminsPage /> },
      {
        path: 'bookings/notifications',
        children: [
          { index: true, element: <BookingNotificationsPage /> },
          {
            path: ':notificationKey',
            element: <BookingNotificationDetailPage />,
          },
        ],
      },
      {
        path: 'user',
        children: [
          {
            path: 'data-privacy',
            element: (
              <FeatureRoute flag="userDataPrivacy" redirectTo="/settings/list">
                <UserDataPrivacyPage />
              </FeatureRoute>
            ),
          },
        ],
      },
      {
        path: 'membership',
        children: [
          {
            path: 'programs',
            children: programRoutes,
          },
          {
            path: 'membership-notifications',
            children: [
              { index: true, element: <MembershipNotificationsPage /> },
              {
                path: ':notificationKey',
                element: <MembershipNotificationDetailPage />,
              },
            ],
          },
          {
            path: 'booklets',
            element: <MembershipBookletsPage />,
          },
          {
            path: 'participation-statuses',
            element: <ParticipationStatusesPage />,
          },
          {
            path: 'profiles-tags',
            element: <MembershipProfileTagsPage />,
          },
          {
            path: 'subscription-tags',
            element: <SubscriptionTagsPage />,
          },
          {
            path: 'complementary-items/list',
            element: <ComplementaryItemsPage />,
          },
        ],
      },

      {
        path: 'integrations',
        children: [
          {
            path: 'sevenrooms',
            element: <IntegrationsSevenroomsPage />,
          },
          {
            path: 'trivec',
            element: <IntegrationsDominoConfigsPage />,
          },
          {
            path: 'adyen',
            element: <IntegrationsAdyenPage />,
          },
          {
            path: 'uber',
            element: <IntegrationsUberPage />,
          },
          {
            path: 'web-booking',
            element: (
              <FeatureRoute flag="webBooking" redirectTo="/settings/list">
                <IntegrationsWebBookingPage />
              </FeatureRoute>
            ),
          },
        ],
      },
      {
        path: 'booking',
        children: [
          { path: 'bookings', element: <BookingSettingsPage /> },
          { path: 'seating-areas', element: <SeatingAreasPage /> },
          {
            path: 'booking-notifications',
            children: [
              { index: true, element: <BookingNotificationsPage /> },
              {
                path: ':notificationKey',
                element: <BookingNotificationDetailPage />,
              },
            ],
          },
          {
            path: 'payment-link',
            element: <PaymentLinkPage />,
          },
          { path: 'availability-viewer', element: <AvailabilityViewerPage /> },
        ],
      },
      {
        path: 'business',
        children: [
          { path: 'company-statuses', element: <CompanyStatusesPage /> },
          {
            path: 'employee-statuses',
            element: <BusinessProfilStatusesPage />,
          },
          {
            path: 'business-notifications',
            element: <BusinessNotificationsPage />,
          },
          {
            path: 'verifier-accounts',
            element: <BusinessVerifierPage />,
          },
          {
            path: 'loyalty-tier',
            element: <BusinessLoyaltyTierPage />,
          },
        ],
      },
      {
        path: 'api',
        children: [
          {
            path: 'external-booking-api-keys',
            element: <ExternalBookingAPIPage />,
          },
        ],
      },
      {
        path: 'legals',
        children: [
          {
            path: 'legal-documents',
            children: legalDocumentRoutes,
          },
        ],
      },
      { index: true, element: <Navigate to="list" replace /> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
]

export default settingsRoutes
